import React, { useState, useEffect } from "react";
import { Button, Alert, Table, Card, CardBody, Input, InputGroup, InputGroupText, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { getConfig } from "../config";
import Loading from "../components/Loading";
import RoleBadge from "../components/RoleBadge";
import { listUserOrganisations } from "../utils/apiService";
import { canEdit, canDelete, canManageMembers } from "../utils/orgRoles";
import { useHistory } from "react-router-dom";

export const OrganisationsComponent = () => {
  const { apiOrigin = "http://localhost:3001", audience } = getConfig();
  const history = useHistory();

  const [state, setState] = useState({
    showResult: false,
    organizations: [],
    filteredOrganizations: [],
    apiMessage: "",
    error: null,
    loading: false,
    searchTerm: "",
    sortField: null,
    sortDirection: "asc",
  });

  const { getAccessTokenSilently, loginWithPopup, getAccessTokenWithPopup } =
    useAuth0();

  // Auto-load organizations on component mount
  useEffect(() => {
    if (audience) {
      callApi();
    }
  }, [audience]);

  // Filter organizations based on search term
  useEffect(() => {
    if (!state.searchTerm) {
      setState(prev => ({ ...prev, filteredOrganizations: prev.organizations }));
    } else {
      const filtered = state.organizations.filter(org =>
        org.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        (org.identifier && org.identifier.toLowerCase().includes(state.searchTerm.toLowerCase())) ||
        (org.location && org.location.toLowerCase().includes(state.searchTerm.toLowerCase()))
      );
      setState(prev => ({ ...prev, filteredOrganizations: filtered }));
    }
  }, [state.organizations, state.searchTerm]);

  const handleSort = (field) => {
    const direction = state.sortField === field && state.sortDirection === "asc" ? "desc" : "asc";
    const sorted = [...state.filteredOrganizations].sort((a, b) => {
      const aVal = a[field] || '';
      const bVal = b[field] || '';
      if (direction === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setState(prev => ({
      ...prev,
      filteredOrganizations: sorted,
      sortField: field,
      sortDirection: direction
    }));
  };

  const handleSearch = (e) => {
    setState(prev => ({ ...prev, searchTerm: e.target.value }));
  };

  const handleConsent = async () => {
    try {
      await getAccessTokenWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const handleLoginAgain = async () => {
    try {
      await loginWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const callApi = async () => {
    setState({
      ...state,
      loading: true,
      error: null,
    });

    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        token = null;
      }

      const apiData = await listUserOrganisations(token);

      const list = Array.isArray(apiData)
        ? apiData
        : (apiData?.data || apiData?.items || apiData?.organisations || apiData?.organizations || []);

      const normalized = (list || []).map((item, index) => {
        const id = item?.id || item?.organisationId || item?.organizationId || index + 1;
        const name = item?.name || item?.organisationName || item?.organizationName || item?.title || `Organisation ${id}`;
        const identifier = item?.identifier || item?.orgIdentifier || item?.organizationIdentifier || '';
        const lat = item?.latitude || 0;
        const lng = item?.longitude || 0;
        const location = (lat !== 0 || lng !== 0) ? `${lat}, ${lng}` : '—';
        const memberCount = Number(item?.memberCount || item?.members?.length || 0) || 0;
        const userRole = item?.userRole || item?.role || null;
        return { id, name, identifier, location, memberCount, userRole };
      });

      setState((prev) => ({
        ...prev,
        showResult: true,
        organizations: normalized,
        filteredOrganizations: normalized,
        apiMessage: apiData,
        loading: false,
      }));
    } catch (error) {
      setState({
        ...state,
        error: error?.message || error?.error || "unknown_error",
        loading: false,
      });
    }
  };

  const handle = (e, fn) => {
    e.preventDefault();
    fn();
  };

  return (
    <>
      <div className="mb-5">
        {state.error === "consent_required" && (
          <Alert color="warning">
            You need to{" "}
            <a
              href="#/"
              className="alert-link"
              onClick={(e) => handle(e, handleConsent)}
            >
              consent to get access to users api
            </a>
          </Alert>
        )}

        {state.error === "login_required" && (
          <Alert color="warning">
            You need to{" "}
            <a
              href="#/"
              className="alert-link"
              onClick={(e) => handle(e, handleLoginAgain)}
            >
              log in again
            </a>
          </Alert>
        )}

        {!audience && (
          <Alert color="warning">
            <p>
              You can't call the API at the moment because your application does
              not have any configuration for <code>audience</code>, or it is
              using the default value of{" "}
              <code>&#123;yourApiIdentifier&#125;</code>. You might get this
              default value if you used the "Download Sample" feature of{" "}
              <a href="https://auth0.com/docs/quickstart/spa/react">
                the quickstart guide
              </a>
              , but have not set an API up in your Auth0 Tenant. You can find
              out more information on{" "}
              <a href="https://auth0.com/docs/api">setting up APIs</a> in the
              Auth0 Docs.
            </p>
            <p>
              The audience is the identifier of the API that you want to call
              (see{" "}
              <a href="https://auth0.com/docs/get-started/dashboard/tenant-settings#api-authorization-settings">
                API Authorization Settings
              </a>{" "}
              for more info).
            </p>

            <p>
              In this sample, you can configure the audience in a couple of
              ways:
            </p>
            <ul>
              <li>
                in the <code>src/index.js</code> file
              </li>
              <li>
                by specifying it in the <code>auth_config.json</code> file (see
                the <code>auth_config.json.example</code> file for an example of
                where it should go)
              </li>
            </ul>
            <p>
              Once you have configured the value for <code>audience</code>,
              please restart the app and try to use the "Ping API" button below.
            </p>
          </Alert>
        )}

        <div className="d-flex justify-content-center mt-4">
          <Button
            color="primary"
            size="lg"
            onClick={() => history.push('/organisations/create')}
            className="me-3"
          >
            <i className="fas fa-plus me-2"></i>
            Create Organisation
          </Button>
        </div>

        {state.loading && (
          <Card className="mt-4">
            <CardBody className="text-center">
              <div className="spinner-border text-primary" role="status">
              </div>
              <p className="mt-3 mb-0">Loading organisations...</p>
            </CardBody>
          </Card>
        )}
      </div>

      <div className="result-block-container">
        {(state.showResult || (!state.loading && audience)) && (
          <div data-testid="api-result" className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              {state.organizations.length > 0 && (
                <InputGroup style={{ maxWidth: "300px" }}>
                  <InputGroupText>🔍</InputGroupText>
                  <Input
                    placeholder="Search organizations..."
                    value={state.searchTerm}
                    onChange={handleSearch}
                  />
                </InputGroup>
              )}
            </div>
            <Table striped responsive hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("name")}
                  >
                    Name {state.sortField === "name" && (state.sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("identifier")}
                  >
                    Identifier {state.sortField === "identifier" && (state.sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("location")}
                  >
                    Location {state.sortField === "location" && (state.sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th>Members</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.filteredOrganizations.length > 0 ? (
                  state.filteredOrganizations.map((org) => (
                    <tr key={org.id}>
                      <td><strong>{org.name}</strong></td>
                      <td>{org.identifier || '—'}</td>
                      <td>{org.location}</td>
                      <td>{org.memberCount}</td>
                      <td>
                        <RoleBadge role={org.userRole} />
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle color="secondary" size="sm" caret>
                            Actions
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => history.push(`/organisations/${org.id}`)}>
                              <i className="fas fa-eye me-2"></i>View
                            </DropdownItem>
                            {canEdit(org.userRole) && (
                              <DropdownItem onClick={() => history.push(`/organisations/${org.id}/edit`)}>
                                <i className="fas fa-edit me-2"></i>Edit
                              </DropdownItem>
                            )}
                            {canManageMembers(org.userRole) && (
                              <DropdownItem onClick={() => history.push(`/organisations/${org.id}/members`)}>
                                <i className="fas fa-users me-2"></i>Manage Members
                              </DropdownItem>
                            )}
                            {canDelete(org.userRole) && (
                              <>
                                <DropdownItem divider />
                                <DropdownItem className="text-danger" onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete "${org.name}"? This action cannot be undone.`)) {
                                    // Delete handled in detail view for now
                                    history.push(`/organisations/${org.id}`);
                                  }
                                }}>
                                  <i className="fas fa-trash me-2"></i>Delete
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      {state.searchTerm ? (
                        <div>
                          <div className="text-muted mb-2">
                            <i className="fas fa-search fa-2x mb-3"></i>
                          </div>
                          <h6 className="text-muted">No organizations match your search</h6>
                          <p className="text-muted mb-0">Try adjusting your search criteria or clear the search to see all organizations.</p>
                        </div>
                      ) : (
                        <div>
                          <div className="text-muted mb-3">
                            <i className="fas fa-building fa-3x mb-3"></i>
                          </div>
                          <h5 className="text-muted mb-2">No Organizations Yet</h5>
                          <p className="text-muted mb-4">
                            You haven't created any organizations yet. Get started by adding your first organization.
                          </p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
};

export default withAuthenticationRequired(OrganisationsComponent, {
  onRedirecting: () => <Loading />,
});
